import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import type { ArsColorCode } from '../../types/barDiagram';
import { arsColorStyles, arsColorLabels } from './barDiagramColors';

/** All selectable p-codes (excluding '-') */
const ALL_PCODES: ArsColorCode[] = [
  'p0', 'p1', 'p2', 'p3', 'p4', 'p5', 'p6', 'p7',
  'p8', 'p9', 'p10', 'p11', 'p14', 'p15', 'p16', 'p22', 'p39',
];

interface Props {
  currentColor: ArsColorCode;
  onSelect: (color: ArsColorCode) => void;
  onClose: () => void;
  anchorRect: DOMRect;
}

export function ColorPicker({ currentColor, onSelect, onClose, anchorRect }: Props) {
  const ref = useRef<HTMLDivElement>(null);

  // Close on click outside or Escape
  useEffect(() => {
    const handleMouseDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onClose();
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  // Position: below the anchor, clamped to viewport
  const top = Math.min(anchorRect.bottom + 4, window.innerHeight - 360);
  const left = Math.min(anchorRect.left, window.innerWidth - 320);

  return createPortal(
    <div
      ref={ref}
      style={{
        position: 'fixed',
        top,
        left,
        zIndex: 9999,
        background: '#1f2937',
        border: '1px solid #374151',
        borderRadius: 8,
        padding: 8,
        boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
        width: 310,
      }}
    >
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 4 }}>
        {ALL_PCODES.map(code => {
          const label = arsColorLabels[code];
          const letter = label.split('—')[0].trim();
          const isSelected = code === currentColor;
          return (
            <button
              key={code}
              onClick={() => onSelect(code)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '4px 6px',
                border: isSelected ? '2px solid #60a5fa' : '1px solid transparent',
                borderRadius: 4,
                background: isSelected ? '#1e3a5f' : 'transparent',
                cursor: 'pointer',
                fontSize: 11,
                color: '#e5e7eb',
                textAlign: 'left',
              }}
              title={label}
            >
              <span
                style={{
                  display: 'inline-block',
                  width: 16,
                  height: 16,
                  border: '1px solid #555',
                  borderRadius: 2,
                  flexShrink: 0,
                  ...arsColorStyles[code],
                }}
              />
              <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {letter}
              </span>
            </button>
          );
        })}
      </div>
    </div>,
    document.body,
  );
}
